import axios, { AxiosResponse } from 'axios'
import { useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation, TFunction } from 'react-i18next'
import { setProfile, clearProfileData } from '../../redux/action/profile'
import useLoadingStore from '../../stores/useLoadingStore'
import ConfigAPI from '../../constant/ConfigAPI'
import isEmail from 'validator/lib/isEmail'

export type Response = { result?: AxiosResponse<any>; error?: any }

export interface Form {
    username?: string
    email?: string
    firstname?: string
    lastname?: string
    department?: string
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.email) {
        errors.email = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.email && !isEmail(values.email)) {
        errors.email = `${t('VALIDATE.MESSAGES.EMAIL')}`
    }
    if (!values.firstname) {
        errors.firstname = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (!values.lastname) {
        errors.lastname = `${t('LABEL.TEXT_REQUIRED')}`
    }
    return errors
}

const useProfile = () => {
    const valueSubmitted = useRef<Form>()
    const [initialData, setInitialData] = useState<ProfileType>({})
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [openDialogBack, setOpenDialogBack] = useState<boolean>(false)
    const [openDialogSave, setOpenDialogSave] = useState<boolean>(false)
    const [openDialogLogout, setOpenDialogLogout] = useState<boolean>(false)
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [alertMessageError, setAlertMessageError] = useState<string>('')
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [statusCode, setStatusCode] = useState<number>()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const loadingStore = useLoadingStore()

    const callServiceGetProfile = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/profile`
            const result = await axios.get(url, ConfigAPI)
            if (result.data) {
                setInitialData(result.data)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin('/profile')
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.NOT_RETRIEVE_DATA'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceEdit = async (data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/profile`
            const reqParams = {
                email: data.email,
                name: data.firstname,
                surname: data.lastname,
            }
            const result = await axios.put(url, reqParams, ConfigAPI)
            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))
            } else {
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceFetchProfile = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/fetchProfile`
            const result = await axios.get(url, ConfigAPI)
            if (result.status === 200) {
                dispatch(setProfile(result.data))
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                globalThis.localStorage.removeItem(`${process.env.REACT_APP_ACCESS_TOKEN_NBTC}`)
                globalThis.localStorage.removeItem(`${process.env.REACT_APP_IS_LOGIN}`)
                if (errMsg.includes('401')) {
                    history.push('/login')
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const handleSubmitForm = (values: any) => {
        valueSubmitted.current = values
        setOpenDialogSave(true)
    }

    const handleClickBack = (dirty: boolean) => {
        // ตอนกดปุ่มยกเลิกที่ form
        setOpenDialogBack(true)
        // if (dirty) {
        //     setOpenDialogBack(true)
        // } else {
        //     setOpenDialogBack(false)
        //     history.push('/')
        // }
    }

    const handleClickCancelBack = () => {
        setOpenDialogBack(false)
    }

    const handleClickConfirmBack = () => {
        setOpenDialogBack(false)
        history.push('/')
    }

    const handleClickCancelSave = () => {
        setOpenDialogSave(false)
    }

    const handleClickConfirmSave = () => {
        callServiceEdit(valueSubmitted.current as Form)
        setOpenDialogSave(false)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        setOpenDialogError(false)
        if (statusCode === 200) {
            callServiceFetchProfile()
        }
        if (statusCode === 401) {
            //reLoginForTokenExpired()
            //callServiceReLogin()
        } else {
            history.go(0)
        }
    }

    // Navigation only — no password or sensitive data involved
    const handleClickChangeISYL = () => {
        history.push('/change-password')
    }

    const handleClickLogout = () => {
        setOpenDialogLogout(true)
    }

    const handleClickCancelLogout = () => {
        setOpenDialogLogout(false)
    }

    const handleClickConfirmLogout = () => {
        setOpenDialogLogout(false)
        globalThis.localStorage.removeItem(`${process.env.REACT_APP_ACCESS_TOKEN_NBTC}`)
        globalThis.localStorage.removeItem(`${process.env.REACT_APP_ACCESS_TOKEN_USER}`)
        globalThis.localStorage.removeItem(`${process.env.REACT_APP_IS_LOGIN}`)
        globalThis.sessionStorage.clear()
        globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
        document.cookie = `${process.env.REACT_APP_TOKEN_VALIDATE}=; path=/; max-age=0`
        dispatch(clearProfileData())
        history.push('/')
    }

    return {
        onSubmitForm: handleSubmitForm,
        onClickLogout: handleClickLogout,
        onClickChangeISYL: handleClickChangeISYL,
        onClickConfirmLogout: handleClickConfirmLogout,
        onClickCancelLogout: handleClickCancelLogout,
        onClickConfirmBack: handleClickConfirmBack,
        onClickCancelBack: handleClickCancelBack,
        onClickConfirmSave: handleClickConfirmSave,
        onClickCancelSave: handleClickCancelSave,
        onClickBack: handleClickBack,
        onClickOk: handleClickOk,
        callServiceGetProfile,
        openDialogLogout,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
    }
}

export default useProfile
